import React, { useCallback, useEffect, useState } from 'react';
import HeaderContentLayout from '../layouts/headerContent';
import { useSelector } from 'react-redux';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { OrganizationSelectors } from '@copilot/common/store/selectors/organization';
import OrganizationDashboardHeader from './header';
import { useFetch, useSearchParams } from '@copilot/common/hooks/common';
import { OrganizationManager } from '@copilot/data';
import { OrganizationActions } from '@copilot/common/store/actions/organization';
import OrganizationDashboardSettings from './settings';
import OrganizationDashboardAllTeamMembers from './orgTeamMembers';
import TemplateMessages from '../settings/template';
import BasicContainer from '@copilot/common/components/containers/basic';
import {
    OrganizationDashboardTabKeys,
    OrganizationDashboardTabLabels,
} from '@copilot/common/utils/constant';
import OrganizationNotificationSettings from './notification';
import ConnectionsPage from '../connections';
import { useLocation, useNavigate } from 'react-router';
import { OrganizationDashboardAllTags } from './tags';
import { useFeatureToggle } from '@copilot/common/hooks/feature';
import { Features } from '@copilot/data/responses/interface';
import OrganizationSummaryV2 from './summaryV2/organizationSummaryV2';
import OrganizationCrmConfiguration from './crmConfiguration';
import Blocklist from './blocklist/blocklist';

type OrganizationDashboardSearchParams = {
    tab: string;
};

export default function OrganizationDashboard() {
    const isBlocklistEnabled = useFeatureToggle(Features.DoNotProspectFeature);
    const activeAdmin = useSelector(OrganizationMemberSelectors.getAdminMember);
    const [, renameOrganization] = useFetch(
        OrganizationManager.renameOrganization,
        OrganizationActions.loadOrganization
    );
    const [tab, setTab] = React.useState<string>('');
    const [searchParams, setSearchParams] = useSearchParams<OrganizationDashboardSearchParams>();
    useEffect(() => {
        setTab(searchParams.tab ?? OrganizationDashboardTabKeys.Summary);
    }, [searchParams]);
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    if (!activeMember) return null;
    const activeOrganization = useSelector(OrganizationSelectors.getActiveOrganization);
    const organizationId = activeOrganization ? activeOrganization.id : '';

    // NOTE: need to use useLocation instead of the redirect value above to get the
    // correct location state when switching between tabs.
    const location = useLocation();
    const navigate = useNavigate();
    const [teamMembersRedirectMemberId, setTeamMembersRedirectMemberId] = useState<string>();
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const memberId: string | undefined =
            location.state?.teamMembersRedirectMemberId ??
            queryParams.get('teamMembersRedirectMemberId');
        if (memberId) {
            setTeamMembersRedirectMemberId(memberId);
            const state = { ...location.state, teamMembersRedirectMemberId: undefined };
            queryParams.delete('teamMembersRedirectMemberId');

            navigate({ ...location, search: queryParams.toString() }, { state });
        }
    }, [location, navigate]);

    const handleTabChange = React.useCallback(
        (key: string) => {
            setSearchParams({ tab: key });
            setTeamMembersRedirectMemberId(undefined);
        },
        [setSearchParams]
    );

    const handleNameUpdate = useCallback(
        (name: string) => activeOrganization?.id && renameOrganization(activeOrganization.id, name),
        [organizationId]
    );
    const showCrmClientIntegration = useFeatureToggle(Features.CRMClientIntegrationFeature);

    if (!organizationId) return null;
    return (
        <HeaderContentLayout>
            <HeaderContentLayout.Header>
                <OrganizationDashboardHeader
                    organizationName={activeOrganization ? activeOrganization.name : ''}
                    onUpdate={handleNameUpdate}
                />
            </HeaderContentLayout.Header>
            <HeaderContentLayout.Tabs activeKey={tab} onChange={handleTabChange}>
                <HeaderContentLayout.TabPanes
                    tab={OrganizationDashboardTabLabels.Summary}
                    key={OrganizationDashboardTabKeys.Summary}
                >
                    <OrganizationSummaryV2 organizationId={organizationId} />
                </HeaderContentLayout.TabPanes>
                <HeaderContentLayout.TabPanes
                    tab={OrganizationDashboardTabLabels.TeamMembers}
                    key={OrganizationDashboardTabKeys.TeamMembers}
                >
                    <OrganizationDashboardAllTeamMembers
                        organizationId={organizationId}
                        initialOrgMemberId={teamMembersRedirectMemberId}
                    />
                </HeaderContentLayout.TabPanes>
                <HeaderContentLayout.TabPanes
                    tab={OrganizationDashboardTabLabels.Tags}
                    key={OrganizationDashboardTabKeys.Tags}
                >
                    <OrganizationDashboardAllTags organizationId={organizationId} />
                </HeaderContentLayout.TabPanes>
                <HeaderContentLayout.TabPanes
                    tab={OrganizationDashboardTabLabels.Template}
                    key={OrganizationDashboardTabKeys.Template}
                >
                    <BasicContainer>
                        <BasicContainer.Header>Quick Responses</BasicContainer.Header>
                        <BasicContainer.Content>
                            <TemplateMessages user={activeMember} showCampaigns hideHeader />
                        </BasicContainer.Content>
                    </BasicContainer>
                </HeaderContentLayout.TabPanes>
                {isBlocklistEnabled ? (
                    <HeaderContentLayout.TabPanes
                        tab={OrganizationDashboardTabLabels.Blocklist}
                        key={OrganizationDashboardTabKeys.Blocklist}
                    >
                        <Blocklist />
                    </HeaderContentLayout.TabPanes>
                ) : null}
                {showCrmClientIntegration ? (
                    <HeaderContentLayout.TabPanes
                        tab={OrganizationDashboardTabLabels.CRMConfiguration}
                        key={OrganizationDashboardTabKeys.CRMConfiguration}
                    >
                        <OrganizationCrmConfiguration organizationId={organizationId} />
                    </HeaderContentLayout.TabPanes>
                ) : null}
                <HeaderContentLayout.TabPanes
                    tab={OrganizationDashboardTabLabels.Settings}
                    key={OrganizationDashboardTabKeys.Settings}
                >
                    <OrganizationDashboardSettings organizationId={organizationId} />
                </HeaderContentLayout.TabPanes>
                <HeaderContentLayout.TabPanes
                    tab={OrganizationDashboardTabLabels.Notification}
                    key={OrganizationDashboardTabKeys.Notification}
                >
                    <OrganizationNotificationSettings organizationId={organizationId} />
                </HeaderContentLayout.TabPanes>
                {!!activeAdmin && (
                    <HeaderContentLayout.TabPanes
                        tab={OrganizationDashboardTabLabels.Connection}
                        key={OrganizationDashboardTabKeys.Connection}
                    >
                        <ConnectionsPage />
                    </HeaderContentLayout.TabPanes>
                )}
            </HeaderContentLayout.Tabs>
        </HeaderContentLayout>
    );
}
